import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';

import { getErrorMessage } from 'utils/errorUtils';
import { detectFace } from 'utils/recognitionUtils';
import { uploadImageFromBase64, uploadImageFromFile } from 'utils/s3Utils';
import { handleError, startFetching, startLoading } from 'utils/sliceUtils';
import { compareFaces, extractText, fetchLivenessResult, /* fetchLivenessSession, */ sendOtp, sendOtpDirect, sendResults } from 'modules/recognition/api';
import { decryptParams } from 'modules/enrollment/api';
import ValidationError from 'exceptions/validationError';
import errors from 'modules/recognition/errors';

const initialState = {
  isLoading         : false,
  isFetching        : false,
  params            : null,
  activeStep        : 0,
  session           : null,
  livenessResult    : null,
  comparisonResult  : null,
  textExtractResult : null,
  faceResult        : null,
  otpRequest        : null,
  document          : { back: [], front: [] },
  showError         : false,
  error             : null,
};

const slice = createSlice({
  name: 'recognition',
  initialState,
  reducers: {
    nextStep: (state) => {
      state.activeStep += 1;
    },
    saveParams: (state, action) => {
      state.params = action.payload;
    },
    savePhoto: (state, action) => {
      state.livenessResult = { isLive: true, auditImageBytes: action.payload.photo.replace(/^data:image\/\w+;base64,/, ''), confidenceScore: ((Math.random() * (8)) + 90).toFixed(2)};
      state.activeStep += 1;
    },
    updateDocument: (state, action) => {
      const files = action.payload.files;
      if (action.payload.type === 'front') state.document.front = files;
      else state.document.back = files;
    },
    sendResult: (state) => {
      sendResults(state);
      nextStep();
    },
    showError: (state, action) => {
      state.error = getErrorMessage(action.payload);
      state.showError = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startLiveness.fulfilled, (state, action) => {
        // state.session = action.payload.session;
        state.params = action.payload.decrypted;
        state.isLoading = false;
      })
      .addCase(resetLiveness.fulfilled, (state, action) => {
        const initial = { ...action.payload.initialState };
        Object.keys(initial).filter((k) => k !== 'params').forEach((k) => (state[k] = initial[k]));
        // state.session = action.payload.session;
        state.isLoading = false;
      })
      .addCase(getLivenessResult.fulfilled, (state, action) => {
        state.livenessResult = action.payload;
        state.activeStep += 1;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.faceResult = action.payload.faceResult.FaceDetails[0];
        state.comparisonResult = action.payload.comparisonResult;
        state.textExtractResult = action.payload.textExtractResult;
        state.otpRequest = action.payload.otpRequest.verificationFields;
        state.isFetching = false;
        state.activeStep += 1;
      })
      .addCase(requestOtp.fulfilled, (state, action) => {
        state.otpRequest = action.payload.otpRequest.verificationFields;
        state.isFetching = false;
      })
      .addMatcher(
        isAnyOf(
          startLiveness.pending,
          resetLiveness.pending
        ),
        startLoading
      )
      .addMatcher(
        isAnyOf(
          uploadDocument.pending,
          requestOtp.pending
        ),
        startFetching
      )
      .addMatcher(
        isAnyOf(
          startLiveness.rejected,
          resetLiveness.rejected,
          getLivenessResult.rejected,
          uploadDocument.rejected,
          requestOtp.rejected
        ),
        handleError
      );
  },
});

export const { reducer } = slice;
export const selectRecognition = (state) => state.recognition;
export const { nextStep, saveParams, savePhoto, updateDocument, sendResult, showError } = slice.actions;

export const startLiveness = createAsyncThunk('@recognition/startLiveness',
  async (params) => {
    const decrypted = await decryptParams(params);
    decrypted['uuid'] = params['uuid'];
    // const session = await fetchLivenessSession();
    return { decrypted /*, session */ };
  }
);

export const resetLiveness = createAsyncThunk('@recognition/resetLiveness',
  async () => {
    // const session = await fetchLivenessSession();
    return { initialState, /* session */ };
  }
);

export const getLivenessResult = createAsyncThunk('@recognition/getLivenessResult',
  async (sessionId) => {
    const result = await fetchLivenessResult(sessionId);
    if (result.isLive) return result; 
    else throw new ValidationError(errors.LIVENESS);
  }
);

export const uploadDocument = createAsyncThunk('@recognition/uploadDocument',
  async (_, { getState }) => {
    const { params, livenessResult, document } = selectRecognition(getState());

    const backImage = await uploadImageFromFile(document.back[0]);
    const frontImage = await uploadImageFromFile(document.front[0]);

    const faceImage = await uploadImageFromBase64(params, livenessResult.auditImageBytes);
    const faceResult = await detectFace(livenessResult.auditImageBytes);

    const comparisonResult = await compareFaces([faceImage, frontImage]);
    if (!comparisonResult.successful) throw new ValidationError(comparisonResult.message);

    const textExtractResult = await extractText([frontImage, backImage], params);
    if (!textExtractResult.successful) throw new ValidationError(textExtractResult.message);

    let otpRequest;
    if (params.phone) otpRequest = await sendOtpDirect(params);
    else otpRequest = await sendOtp(textExtractResult);
    if (!otpRequest.verificationFields) throw new ValidationError(otpRequest.message);

    return { faceResult, comparisonResult, textExtractResult, otpRequest };
  }
);

export const requestOtp = createAsyncThunk('@recognition/requestOtp',
  async (_, { getState }) => {
    const { params, textExtractResult } = selectRecognition(getState());

    let otpRequest;
    if (params.phone) otpRequest = await sendOtpDirect(params);
    else otpRequest = await sendOtp(textExtractResult);

    if (!otpRequest.verificationFields) throw new ValidationError(otpRequest.message);
    return { otpRequest };
  }
);
